/**
 * EcosystemSystem — the living 4-tier food chain.
 *
 * Spawn pool isn't a flat list — each tier has its own population pool
 * that decrements on kill (by player OR by another creature) and regens
 * over time. Tier-N hunters scan the world for tier-(N-1) prey and eat
 * them on contact, which is what makes the ecosystem visibly alive.
 *
 * What this system does NOT do: rendering. Creatures are ECS entities
 * with CreatureComp + Position + Renderable, drawn by CambrianStage's
 * own renderer. EcosystemSystem only tracks population, spawns, and
 * runs the NPC predation loop.
 *
 * For M2 this is the heart of the stage: turn it on and creatures
 * eat each other on screen with no player input required.
 */

import { bus } from '@/core/EventBus';
import type { World } from '@/core/ECS';
import { Position } from '@/components';
import type { CreatureC, CreatureDef, CreatureTier } from '@/stages/cambrian/types';
import { CreatureComp } from '@/stages/cambrian/types';

export interface TierConfig {
  cap: number;
  /** Creatures regenerated per second when below cap. */
  regenPerSec: number;
}

export type EcosystemConfig = Record<CreatureTier, TierConfig>;

export class EcosystemSystem {
  private populations: Record<CreatureTier, number> = { 1: 0, 2: 0, 3: 0, 4: 0 };
  private regenAccum: Record<CreatureTier, number> = { 1: 0, 2: 0, 3: 0, 4: 0 };
  private config: EcosystemConfig;
  /** Per-tier list of creature defs to roll from when spawning. */
  private defsByTier: Record<CreatureTier, CreatureDef[]>;
  /** Caller-supplied factory that materializes a creature in the world. */
  private spawnFn: (def: CreatureDef) => number;

  constructor(
    config: EcosystemConfig,
    defs: CreatureDef[],
    spawnFn: (def: CreatureDef) => number,
  ) {
    this.config = config;
    this.spawnFn = spawnFn;
    this.defsByTier = { 1: [], 2: [], 3: [], 4: [] };
    for (const d of defs) this.defsByTier[d.tier].push(d);
  }

  /** Initial fill — populate each tier to a fraction of cap on stage start. */
  primeInitialPopulation(fillFrac = 0.6): void {
    for (const t of [1, 2, 3, 4] as CreatureTier[]) {
      const want = Math.floor(this.config[t].cap * fillFrac);
      // Apex (tier 4) defers to the stage's apex-spawn timer; spawn 0 at prime.
      if (t === 4) continue;
      for (let i = 0; i < want; i++) this.spawnAtTier(t);
    }
    this.emitChange();
  }

  /** Roll a random creature def for a tier and ask the stage to spawn it. */
  /** Stage-supplied gate on which defs may spawn right now. */
  private allow: (def: CreatureDef) => boolean = () => true;
  setAllow(fn: (def: CreatureDef) => boolean): void { this.allow = fn; }

  spawnAtTier(tier: CreatureTier): number {
    const pool = this.defsByTier[tier].filter((d) => this.allow(d));
    if (pool.length === 0) return 0;
    let roll = Math.random() * pool.reduce((a, d) => a + (d.weight ?? 1), 0);
    let def = pool[pool.length - 1];
    for (const d of pool) { roll -= d.weight ?? 1; if (roll <= 0) { def = d; break; } }
    const id = this.spawnFn(def);
    if (id > 0) {
      this.populations[tier] += 1;
    }
    return id;
  }

  /** Spawn a specific creature by def id. */
  spawnSpecific(defId: string): number {
    for (const t of [1, 2, 3, 4] as CreatureTier[]) {
      const def = this.defsByTier[t].find((d) => d.id === defId);
      if (def) {
        const id = this.spawnFn(def);
        if (id > 0) this.populations[t] += 1;
        return id;
      }
    }
    return 0;
  }

  /** Called by the stage whenever a creature is destroyed (any reason). */
  recordRemoval(tier: CreatureTier): void {
    this.populations[tier] = Math.max(0, this.populations[tier] - 1);
    this.emitChange();
  }

  /** Per-frame: regen each tier toward cap.
   *  Regen scales up when the food source (tier-1) is abundant, down when scarce. */
  tick(dt: number): void {
    for (const t of [1, 2, 3, 4] as CreatureTier[]) {
      const cap = this.config[t].cap;
      const pop = this.populations[t];
      if (pop >= cap) continue;
      let rate = this.config[t].regenPerSec;
      // Food-source modulation: if tier > 1, modulate by the lower tier's
      // health relative to its cap. Starving tiers shrink regen by up to 80%.
      if (t > 1) {
        const lower = (t - 1) as CreatureTier;
        const lowerHealth = this.populations[lower] / Math.max(1, this.config[lower].cap);
        rate *= 0.2 + 0.8 * Math.min(1, lowerHealth);
      }
      this.regenAccum[t] += rate * dt;
      while (this.regenAccum[t] >= 1 && this.populations[t] < cap) {
        this.regenAccum[t] -= 1;
        this.spawnAtTier(t);
      }
    }
  }

  /** NPC predation loop. Called by the stage with the live creature roster.
   *  Each hunter scans for nearby prey one tier below and pursues / bites. */
  runPredation(world: World, dt: number): void {
    const positions = new Map<number, { x: number; y: number; c: CreatureC }>();
    for (const e of world.query([CreatureComp, Position])) {
      const c = world.get(e, CreatureComp)!;
      const p = world.get(e, Position)!;
      positions.set(e, { x: p.x, y: p.y, c });
    }

    // For each hunter / apex with eatsTiers, find the nearest prey.
    for (const [hunterId, hunter] of positions) {
      const c = hunter.c;
      if (c.eatsTiers.length === 0) continue;
      if (c.behavior === 'passive') continue;
      // Tick hunt AI cooldown
      c.ai = Math.max(0, c.ai - dt);

      // Find nearest valid prey.
      let bestId = 0;
      let bestDist = Infinity;
      for (const [preyId, prey] of positions) {
        if (preyId === hunterId) continue;
        if (!c.eatsTiers.includes(prey.c.tier)) continue;
        const dx = prey.x - hunter.x;
        const dy = prey.y - hunter.y;
        const d2 = dx * dx + dy * dy;
        if (d2 < bestDist) { bestDist = d2; bestId = preyId; }
      }
      c.target = bestId;
      if (bestId === 0) {
        // wander
        if (c.ai === 0) {
          c.heading = Math.random() * Math.PI * 2;
          c.ai = 1.5 + Math.random() * 2.0;
        }
        continue;
      }

      const prey = positions.get(bestId)!;
      const dx = prey.x - hunter.x;
      const dy = prey.y - hunter.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > 0) c.heading = Math.atan2(dy, dx);

      // Bite range — eat the prey if we touch it.
      const biteR = (c.size + prey.c.size) * 0.9;
      if (dist < biteR) {
        // NPC kill — destroy prey, notify ecosystem, emit kill event.
        bus.emit('cambrian:kill', {
          killerId: hunterId,
          victimId: bestId,
          victimTier: prey.c.tier,
          victimDefId: prey.c.defId,
          byPlayer: false,
          x: prey.x,
          y: prey.y,
          biomass: 0,
          xp: 0,
        });
        // Stage handles the actual world destroy + recordRemoval.
      }
    }
  }

  getPopulation(tier: CreatureTier): number { return this.populations[tier]; }
  getCap(tier: CreatureTier): number { return this.config[tier].cap; }
  /** Retune a tier's cap at runtime (the stage scales hunters with the
   *  player's size). Over-cap populations just stop regenerating. */
  setCap(tier: CreatureTier, cap: number): void {
    if (this.config[tier].cap === cap) return;
    this.config[tier].cap = cap;
    this.emitChange();
  }
  getConfig(): EcosystemConfig { return this.config; }

  private emitChange(): void {
    bus.emit('cambrian:ecosystem:changed', {
      populations: { ...this.populations },
      caps: {
        1: this.config[1].cap,
        2: this.config[2].cap,
        3: this.config[3].cap,
        4: this.config[4].cap,
      },
    });
  }
}
